import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';

export const ACCOUNT_BLOCK_KEY = 'pulse_account_block';

export function isSuperAdminClaims(claims) {
    if (!claims) return false;
    return claims.platformRole === 'superadmin' || claims.superAdmin === true || claims.role === 'superadmin';
}

export function blockedTenantMessage(status) {
    const s = String(status || '').toLowerCase();
    if (s === 'suspended') {
        return 'This insurance account is suspended. Staff cannot sign in. Contact Pulse Insurance.';
    }
    if (s === 'deleted') {
        return 'This insurance account has been closed. Staff cannot sign in.';
    }
    return null;
}

export async function companyTenantBlockReason(tenantId) {
    if (!tenantId) return 'This staff login is not linked to an insurance company.';
    const snap = await getDoc(doc(db, 'tenants', tenantId));
    if (!snap.exists()) return 'This insurance account was not found. Staff cannot sign in.';
    return blockedTenantMessage(snap.data().status);
}

function sessionFromUser(user, claims) {
    return {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || claims.displayName || user.email,
        role: claims.role || 'broker',
        tenantId: claims.tenantId || null,
        companyName: claims.companyName || 'Insurance Company',
        superAdmin: isSuperAdminClaims(claims)
    };
}

export function useCompanySession() {
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) {
                navigate('/login');
                setLoading(false);
                return;
            }
            const token = await user.getIdTokenResult();
            if (isSuperAdminClaims(token.claims)) {
                navigate('/super-admin');
                setLoading(false);
                return;
            }
            if (!token.claims.tenantId) {
                navigate('/login');
                setLoading(false);
                return;
            }
            const blocked = await companyTenantBlockReason(token.claims.tenantId);
            if (blocked) {
                try { sessionStorage.setItem(ACCOUNT_BLOCK_KEY, blocked); } catch (e) { /* ignore */ }
                await signOut(auth);
                navigate('/login');
                setLoading(false);
                return;
            }
            const sess = sessionFromUser(user, token.claims);
            try {
                const tenantSnap = await getDoc(doc(db, 'tenants', token.claims.tenantId));
                if (tenantSnap.exists() && tenantSnap.data().companyName) {
                    sess.companyName = tenantSnap.data().companyName;
                    sess.locale = tenantSnap.data().locale || 'en';
                }
            } catch (e) { /* claims fallback */ }
            setSession(sess);
            setLoading(false);
        });
        return () => unsub();
    }, [navigate]);

    return { session, loading };
}

export function useSuperAdminSession() {
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) {
                navigate('/super-admin/login');
                setLoading(false);
                return;
            }
            const token = await user.getIdTokenResult();
            if (!isSuperAdminClaims(token.claims)) {
                navigate('/login');
                setLoading(false);
                return;
            }
            setSession(sessionFromUser(user, token.claims));
            setLoading(false);
        });
        return () => unsub();
    }, [navigate]);

    return { session, loading };
}
